import React, { useState, useEffect } from "react";
import { X, Phone, Edit, Trash2, Calendar, Loader2, CheckSquare, Square, ClipboardList, History, Info, CalendarDays, Plus, MapPin } from "lucide-react";

import { i18n } from "../../translate/i18n";
import ContactModal from "../ContactModal";
import AppointmentModal from "../AppointmentModal";
import { statusMeta, DEFAULT_REASON_COLOR } from "../AppointmentCalendar/appointmentUtils";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import ContactAvatar from "../ContactAvatar";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const ContactDrawer = ({ open, handleDrawerClose, contact, loading }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("info"); // info, timeline, notes, tasks, appointments

  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDueDate, setNewTaskDueDate] = useState("");

  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);

  const loadTimeline = async () => {
    if (!contact?.id) return;
    setLoadingTimeline(true);
    try {
      const { data } = await api.get(`/contacts/${contact.id}/timeline`);
      setTimeline(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const loadTasks = async () => {
    if (!contact?.id) return;
    setLoadingTasks(true);
    try {
      const { data } = await api.get(`/contacts/${contact.id}/tasks`);
      setTasks(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingTasks(false);
    }
  };

  const loadAppointments = async () => {
    if (!contact?.id) return;
    setLoadingAppointments(true);
    try {
      const { data } = await api.get(`/contacts/${contact.id}/appointments`);
      setAppointments(data.appointments || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingAppointments(false);
    }
  };

  useEffect(() => {
    if (open && contact?.id) {
      if (activeTab === "timeline" || activeTab === "notes") {
        loadTimeline();
      } else if (activeTab === "tasks") {
        loadTasks();
      } else if (activeTab === "appointments") {
        loadAppointments();
      }
    }
  }, [open, contact?.id, activeTab]);

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim() || !newTaskDueDate) return;
    try {
      const { data } = await api.post(`/contacts/${contact.id}/tasks`, {
        title: newTaskTitle,
        dueDate: newTaskDueDate
      });
      setTasks((prev) => [...prev, data].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)));
      setNewTaskTitle("");
      setNewTaskDueDate("");
    } catch (err) {
      toastError(err);
    }
  };

  const handleToggleTaskStatus = async (task) => {
    const nextStatus = task.status === "pending" ? "completed" : "pending";
    try {
      const { data } = await api.put(`/contacts/tasks/${task.id}`, {
        status: nextStatus
      });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? data : t)));
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await api.delete(`/contacts/tasks/${taskId}`);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
    } catch (err) {
      toastError(err);
    }
  };

  const renderTimelineItem = (item) => {
    const formattedDate = new Date(item.createdAt).toLocaleString();
    if (item.type === "note") {
      return (
        <div key={item.id} className="relative pl-6 pb-4 last:pb-0">
          <div className="absolute left-[9px] top-2 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700" />
          <div className="absolute left-0 top-1.5 p-0.5 bg-amber-100 dark:bg-amber-950 text-amber-600 dark:text-amber-400 rounded-full border border-white dark:border-slate-800 shadow-sm">
            <Edit className="w-3 h-3" />
          </div>
          <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/10 p-3 shadow-sm">
            <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-1">
              Nota Interna - {item.agentName}
            </p>
            <p className="text-xs text-slate-800 dark:text-slate-200 leading-relaxed whitespace-pre-wrap">{item.body}</p>
            <p className="text-[9px] text-slate-400 mt-1.5 font-medium">{formattedDate}</p>
          </div>
        </div>
      );
    }

    let title = `Ticket ${item.status}`;
    let iconColor = "text-indigo-600 bg-indigo-100 dark:bg-indigo-950 dark:text-indigo-400";
    if (item.status === "closed") {
      title = "Ticket Cerrado";
      iconColor = "text-emerald-600 bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-400";
    } else if (item.status === "open") {
      title = "Ticket Abierto";
      iconColor = "text-indigo-600 bg-indigo-100 dark:bg-indigo-950 dark:text-indigo-400";
    } else if (item.status === "pending") {
      title = "Ticket Pendiente";
      iconColor = "text-sky-600 bg-sky-100 dark:bg-sky-950 dark:text-sky-400";
    }

    let detailsText = `Asignado a: ${item.agentName}`;
    if (item.queueName) {
      detailsText += ` / Fila: ${item.queueName}`;
    }
    if (item.whatsappName) {
      detailsText += ` (${item.whatsappName})`;
    }

    return (
      <div key={item.id} className="relative pl-6 pb-4 last:pb-0">
        <div className="absolute left-[9px] top-2 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700" />
        <div className={`absolute left-0 top-1.5 p-0.5 rounded-full border border-white dark:border-slate-800 shadow-sm ${iconColor}`}>
          <History className="w-3 h-3" />
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-sm">
          <p className="text-xs font-bold text-slate-800 dark:text-slate-200">{title}</p>
          <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5 leading-relaxed">{detailsText}</p>
          <p className="text-[9px] text-slate-400 mt-1.5 font-medium">{formattedDate}</p>
        </div>
      </div>
    );
  };

  return (
    <>
      {open && (
        <div className="absolute inset-y-0 right-0 w-80 flex flex-col border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 z-20 shadow-xl">
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3.5 border-b border-slate-200 dark:border-slate-800 flex-none">
            <button
              onClick={handleDrawerClose}
              className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {i18n.t("contactDrawer.header")}
            </h3>
          </div>

          {loading ? (
            <ContactDrawerSkeleton />
          ) : (
            <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-800/30 p-4 space-y-4">
              {/* Contact card */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 flex flex-col items-center gap-2.5 shadow-sm">
                <ContactAvatar
                  src={contact.profilePicUrl}
                  alt={contact.name}
                  className="rounded-full object-cover ring-4 ring-white dark:ring-slate-800 shadow-sm"
                  fallbackClassName="rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold ring-4 ring-white dark:ring-slate-800 shadow-sm"
                  size="lg"
                  contactId={contact.id}
                />
                <p className="text-base font-bold text-slate-900 dark:text-slate-100 text-center truncate w-full">{contact.name}</p>
                <div className="flex items-center gap-1.5 w-full">
                  <a
                    href={`tel:${contact.number}`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-semibold text-xs rounded-lg transition-colors no-underline border border-transparent dark:border-slate-700"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    {contact.number}
                  </a>
                  <button
                    onClick={() => setModalOpen(true)}
                    className="flex items-center justify-center p-1.5 border border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
                    title={i18n.t("contactDrawer.buttons.edit")}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} contactId={contact.id} />

              {/* Tabs header (3-column grid: 5 tabs over 2 rows) */}
              <div className="grid grid-cols-3 gap-1.5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl p-1.5 shadow-sm">
                <button
                  onClick={() => setActiveTab("info")}
                  className={`flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    activeTab === "info"
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                  }`}
                >
                  <Info className="w-3.5 h-3.5" />
                  Info
                </button>
                <button
                  onClick={() => setActiveTab("timeline")}
                  className={`flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    activeTab === "timeline"
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                  }`}
                >
                  <History className="w-3.5 h-3.5" />
                  Historial
                </button>
                <button
                  onClick={() => setActiveTab("notes")}
                  className={`flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    activeTab === "notes"
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                  }`}
                >
                  <Edit className="w-3.5 h-3.5" />
                  Notas
                </button>
                <button
                  onClick={() => setActiveTab("tasks")}
                  className={`flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    activeTab === "tasks"
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                  }`}
                >
                  <ClipboardList className="w-3.5 h-3.5" />
                  Tareas
                </button>
                <button
                  onClick={() => setActiveTab("appointments")}
                  className={`flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    activeTab === "appointments"
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                  }`}
                >
                  <CalendarDays className="w-3.5 h-3.5" />
                  Citas
                </button>
              </div>

              {/* Info tab */}
              {activeTab === "info" && (
                <>
                  {contact?.extraInfo?.length > 0 ? (
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 space-y-3 shadow-sm">
                      <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        {i18n.t("contactDrawer.extraInfo")}
                      </p>
                      <div className="space-y-2">
                        {contact.extraInfo.map((info) => (
                          <div key={info.id} className="rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                            <p className="text-[9px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">{info.name}</p>
                            <div className="text-xs text-slate-800 dark:text-slate-200">
                              <MarkdownWrapper>{info.value}</MarkdownWrapper>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400 text-xs">
                      No hay información adicional para este contacto.
                    </div>
                  )}
                </>
              )}

              {/* Timeline tab */}
              {activeTab === "timeline" && (
                <div className="space-y-3">
                  <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider pl-1">
                    Línea de Tiempo del Cliente
                  </p>
                  {loadingTimeline ? (
                    <div className="flex justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : timeline.length > 0 ? (
                    <div className="pt-2">
                      {timeline.map((item) => renderTimelineItem(item))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400 text-xs">
                      No se registra historial de actividades aún.
                    </div>
                  )}
                </div>
              )}

              {/* Notes tab */}
              {activeTab === "notes" && (
                <div className="space-y-3">
                  <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider pl-1">
                    Notas Internas Registradas
                  </p>
                  {loadingTimeline ? (
                    <div className="flex justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : timeline.filter(item => item.type === "note").length > 0 ? (
                    <div className="space-y-3">
                      {timeline
                        .filter(item => item.type === "note")
                        .map((note) => (
                          <div key={note.id} className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/10 p-3 shadow-sm">
                            <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-1">
                              Por: {note.agentName}
                            </p>
                            <p className="text-xs text-slate-800 dark:text-slate-200 leading-relaxed whitespace-pre-wrap">{note.body}</p>
                            <p className="text-[9px] text-slate-400 mt-2 font-medium">
                              {new Date(note.createdAt).toLocaleString()}
                            </p>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400 text-xs">
                      No hay notas internas guardadas para este contacto.
                    </div>
                  )}
                </div>
              )}

              {/* Tasks tab */}
              {activeTab === "tasks" && (
                <div className="space-y-4">
                  {/* Create Task Form */}
                  <form onSubmit={handleAddTask} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 space-y-3 shadow-sm">
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                      Nueva Tarea / Recordatorio
                    </p>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="¿Qué debes hacer?"
                        required
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)}
                        className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <input
                          type="datetime-local"
                          required
                          value={newTaskDueDate}
                          onChange={(e) => setNewTaskDueDate(e.target.value)}
                          className="flex-1 px-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-lg transition-colors shadow-sm"
                      >
                        Crear Recordatorio
                      </button>
                    </div>
                  </form>

                  {/* Tasks List */}
                  <div className="space-y-2">
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider pl-1">
                      Tareas de Seguimiento
                    </p>
                    {loadingTasks ? (
                      <div className="flex justify-center py-6">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                      </div>
                    ) : tasks.length > 0 ? (
                      <div className="space-y-2">
                        {tasks.map((task) => {
                          const isCompleted = task.status === "completed";
                          return (
                            <div key={task.id} className={`flex items-start gap-2.5 p-3 rounded-xl border transition-all ${
                              isCompleted
                                ? "bg-slate-100/50 dark:bg-slate-800/30 border-slate-200 dark:border-slate-800 opacity-60"
                                : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm"
                            }`}>
                              <button
                                onClick={() => handleToggleTaskStatus(task)}
                                className={`mt-0.5 flex-shrink-0 transition-colors ${
                                  isCompleted ? "text-indigo-600" : "text-slate-400 hover:text-slate-600"
                                }`}
                              >
                                {isCompleted ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                              </button>
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs font-medium text-slate-900 dark:text-slate-100 leading-tight ${isCompleted ? "line-through" : ""}`}>
                                  {task.title}
                                </p>
                                <p className="text-[9px] text-slate-400 dark:text-slate-500 mt-1 flex items-center gap-1">
                                  <span>📅 {new Date(task.dueDate).toLocaleString()}</span>
                                  <span>• Asignado a: {task.user?.name || "Agente"}</span>
                                </p>
                              </div>
                              <button
                                onClick={() => handleDeleteTask(task.id)}
                                className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all flex-shrink-0"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-400 text-xs">
                        No hay tareas pendientes asignadas.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Appointments tab */}
              {activeTab === "appointments" && (
                <div className="space-y-4">
                  <button
                    onClick={() => { setSelectedAppointmentId(null); setAppointmentModalOpen(true); }}
                    className="w-full flex items-center justify-center gap-1.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-lg transition-colors shadow-sm"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Agendar nueva cita
                  </button>

                  <div className="space-y-2">
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider pl-1">
                      Citas del Contacto
                    </p>
                    {loadingAppointments ? (
                      <div className="flex justify-center py-6">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                      </div>
                    ) : appointments.length > 0 ? (
                      <div className="space-y-2">
                        {appointments.map((appointment) => {
                          const meta = statusMeta(appointment.status);
                          const color = appointment.reason?.color || DEFAULT_REASON_COLOR;
                          return (
                            <button
                              key={appointment.id}
                              onClick={() => { setSelectedAppointmentId(appointment.id); setAppointmentModalOpen(true); }}
                              className="w-full flex items-start gap-2.5 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                            >
                              <span
                                className="w-1 self-stretch rounded-full flex-shrink-0"
                                style={{ backgroundColor: color }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs font-medium text-slate-900 dark:text-slate-100 leading-tight ${
                                  appointment.status === "cancelled" ? "line-through opacity-60" : ""
                                }`}>
                                  {appointment.reason?.name || "Cita"}
                                </p>
                                <p className="text-[9px] text-slate-400 dark:text-slate-500 mt-1 flex flex-wrap items-center gap-x-2">
                                  <span>📅 {new Date(appointment.scheduledAt).toLocaleString()}</span>
                                  {appointment.professional?.name && (
                                    <span>• {appointment.professional.name}</span>
                                  )}
                                  {appointment.location && (
                                    <span className="flex items-center gap-0.5">
                                      <MapPin className="w-2.5 h-2.5" />
                                      {appointment.location}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold border flex-shrink-0 ${meta.badge}`}>
                                {meta.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-400 text-xs">
                        Este contacto no tiene citas registradas.
                      </div>
                    )}
                  </div>

                  <AppointmentModal
                    open={appointmentModalOpen}
                    onClose={() => { setAppointmentModalOpen(false); setSelectedAppointmentId(null); }}
                    appointmentId={selectedAppointmentId}
                    initialContact={contact}
                    onSaved={loadAppointments}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ContactDrawer;
